<!--
  LinkAccountModal.vue
  Vue 3 modal for linking Google account to existing password-based account

  Features:
  - Prompts user to enter password to verify account ownership
  - Calls backend link-google endpoint
  - Handles success and error states

  Props:
  - email: The email address of the existing account
  - googleCredential: The Google ID token to link

  Emits:
  - success: When linking is successful, passes user data
  - cancel: When user cancels the linking flow

  Usage:
  <LinkAccountModal
    v-if="showLinkModal"
    :email="linkEmail"
    :google-credential="linkCredential"
    @success="handleLinkSuccess"
    @cancel="showLinkModal = false"
  />
-->

<script setup lang="ts">
import { ref } from 'vue'
import BaseModal from '../modals/BaseModal.vue'

// Props
const props = defineProps<{
  email: string
  googleCredential: string
}>()

// Emits
const emit = defineEmits<{
  success: [user: unknown]
  cancel: []
}>()

// Component state
const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const errorMessage = ref('')

// API URL configuration
const isDevPort = window.location.port && !['80', '443', ''].includes(window.location.port)
const API_URL = (window.location.hostname === 'localhost' && isDevPort)
  ? `${window.location.protocol}//localhost:8000/api`
  : `${window.location.origin}/api`

/**
 * Handle form submission to link Google account
 */
async function handleLink() {
  if (!password.value) {
    errorMessage.value = 'Please enter your password'
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    const response = await fetch(`${API_URL}/auth/link-google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential: props.googleCredential,
        password: password.value,
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      const errorMsg = typeof data.detail === 'string'
        ? data.detail
        : data.detail?.message || data.message || 'Failed to link account'
      throw new Error(errorMsg)
    }

    // Store tokens
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)

    // Emit success
    emit('success', data.user)
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to link account'
    errorMessage.value = message
  } finally {
    loading.value = false
  }
}

/**
 * Handle cancel
 */
function handleCancel() {
  emit('cancel')
}
</script>

<template>
  <BaseModal
    :is-open="true"
    title="Link Google Account"
    max-width="450px"
    :closable="!loading"
    @close="handleCancel"
  >
    <div class="link-account-content">
      <div class="info-box">
        <span class="material-symbols-outlined">info</span>
        <p>
          An account with email <strong>{{ email }}</strong> already exists.
          Enter your password to link your Google account for faster login.
        </p>
      </div>

      <form @submit.prevent="handleLink" class="link-form">
        <div class="form-group">
          <label for="link-password">Password</label>
          <div class="password-input-wrapper">
            <input
              id="link-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Enter your password"
              :disabled="loading"
              autocomplete="current-password"
              required
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              tabindex="-1"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
            >
              <span class="material-symbols-outlined">
                {{ showPassword ? 'visibility_off' : 'visibility' }}
              </span>
            </button>
          </div>
        </div>

        <div v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </div>

        <div class="button-row">
          <button
            type="button"
            class="btn-secondary"
            :disabled="loading"
            @click="handleCancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn-primary"
            :disabled="loading || !password"
          >
            <span v-if="loading" class="loading-spinner"></span>
            <span>{{ loading ? 'Linking...' : 'Link Account' }}</span>
          </button>
        </div>
      </form>
    </div>
  </BaseModal>
</template>

<style scoped>
.link-account-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-box {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
}

.info-box .material-symbols-outlined {
  color: #60a5fa;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.info-box p {
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.info-box strong {
  color: #60a5fa;
}

.link-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  flex: 1;
  padding-right: 44px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.password-toggle:hover {
  color: rgba(255, 255, 255, 0.8);
}

.password-toggle .material-symbols-outlined {
  font-size: 1.25rem;
}

.form-group input {
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  font-size: 1rem;
  transition: all 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(232, 197, 71, 0.5);
  background: rgba(255, 255, 255, 0.08);
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.form-group input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
}

.button-row {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(13, 31, 45, 0.3);
  border-top-color: #0d1f2d;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .button-row {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>
